import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {  useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import useLocations from "@modules/organizationalStructure/locations/hooks/useLocations";
import useFormHook from "@shared/hooks/useFormHook";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { companiesService } from "@shared/services/companiesService";
import CustomDatePicker from "@shared/components/CustomDatePicker";

export default function CompanyCentersForm({ onClose, onConfirm, editData = null, isEdit = false }) {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);

    const validationSchema = yup.object().shape({
        costCenterCode: yup.string().required(t('common.required')).min(5, t('common.minLength') + " " + "5"),
        name: yup.string().required(t('common.required')),
    });

    const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        criteriaMode: 'all',
        defaultValues: {
            costCenterCode: editData?.officeNo || '',
            name: editData?.name || '',
            address: editData?.address || '',
            phone: editData?.phone || '',
            region: '',
            subregion: '',
            cityId: '',
            postalCode: editData?.postCode || '',
            dateClosing: editData?.dateClosing || '',
            activity: editData?.activity ?? 0,
            reasonClosing: editData?.reasonClosing || ''
        }
    });

    // Location management
    const {
        fetchCountries,
        fetchRegionsByCountry,
        fetchSubRegionsByRegion,
        fetchCitiesbySubRegion,
        countryOptions,
        regionOptions,
        subRegionOptions,
        cityOptions,
        selectedCountry,
        selectedRegion,
        selectedSubRegion,
        selectedCity,
        setSelectedCountry,
        setSelectedRegion,
        setSelectedSubRegion,
        setSelectedCity,
    } = useLocations();

    // Watch form values for location fields
    const region = watch('region');
    const subregion = watch('subregion');
    const cityId = watch('cityId');

    // Set Bulgaria as default country and fetch regions
    useEffect(() => {
        const setDefaultCountry = async () => {
            try {
                await fetchCountries();
                if (countryOptions.length > 0) {
                    const bulgaria = countryOptions.find(option => option.value === 'BG');
                    if (bulgaria) {
                        setValue('country', bulgaria.value);
                        setSelectedCountry(bulgaria.value);
                        await fetchRegionsByCountry(bulgaria.value);
                    }
                }
            } catch (error) {
                console.error('Error setting default country:', error);
            }
        };

        setDefaultCountry();
    }, [countryOptions.length]);

    // Handle region change
    useEffect(() => {
        if (region) {
            setSelectedRegion(region);
            setValue('subregion', '');
            setValue('cityId', '');
            setSelectedSubRegion(null);
            setSelectedCity('');
            fetchSubRegionsByRegion(region);
        }
    }, [region]);

    // Handle subregion change
    useEffect(() => {
        if (subregion) {
            setSelectedSubRegion(subregion);
            setValue('cityId', '');
            setSelectedCity('');
            fetchCitiesbySubRegion(subregion);
        }
    }, [subregion]);

    // Handle city change - fetch city details and get postal code
    useEffect(() => {
        if (cityId) {
            setSelectedCity(cityId);

            // Fetch city details by ID to get postal code
            const fetchCityDetails = async () => {
                try {
                    const cityData = await companiesService.getCityById(cityId);

                    if (cityData && cityData.postal) {
                        setValue('postalCode', cityData.postal);
                    }
                } catch (error) {
                    console.error('Error fetching city details:', error);
                }
            };

            fetchCityDetails();
        }
    }, [cityId, setValue]);

    // Load edit data when component mounts
    useEffect(() => {
        if (editData) {
            loadEditData();
        }
    }, [editData]);

    const loadEditData = async () => {
        try {
            // Set form values immediately
            setValue('costCenterCode', editData.officeNo || '');
            setValue('name', editData.name || '');
            setValue('address', editData.address || '');
            setValue('phone', editData.phone || '');
            setValue('postalCode', editData.postCode || '');
            setValue('dateClosing', editData.dateClosing || '');
            setValue('activity', editData.activity ?? 0);
            setValue('reasonClosing', editData.reasonClosing || '');

            // Load location data if city exists
            if (editData.city) {
                loadLocationData(editData.city);
            }
        } catch (error) {
            console.error('Error loading edit data:', error);
        }
    };

    const loadLocationData = async (cityData) => {
        try {
            // Set country (Bulgaria)
            setValue('country', 'BG');
            setSelectedCountry('BG');
            
            // Load regions first
            await fetchRegionsByCountry('BG');
            
            // Set region when options are available
            if (cityData.regionId && regionOptions.length > 0) {
                setValue('region', cityData.regionId);
                setSelectedRegion(cityData.regionId);
                
                // Load subregions
                await fetchSubRegionsByRegion(cityData.regionId);
                
                // Set subregion when options are available
                if (cityData.subregionId && subRegionOptions.length > 0) {
                    setValue('subregion', cityData.subregionId);
                    setSelectedSubRegion(cityData.subregionId);
                    
                    // Load cities
                    await fetchCitiesbySubRegion(cityData.subregionId);
                    
                    // Set city when options are available
                    if (cityData.id && cityOptions.length > 0) {
                        setValue('cityId', cityData.id);
                        setSelectedCity(cityData.id);
                    }
                }
            }
        } catch (error) {
            console.error('Error loading location data:', error);
        }
    };

    // Set location values when options become available (for edit mode)
    useEffect(() => {
        if (editData?.city && regionOptions.length > 0 && !selectedRegion) {
            setValue('region', editData.city.regionId);
            setSelectedRegion(editData.city.regionId);
        }
    }, [regionOptions.length, editData?.city, selectedRegion]);

    useEffect(() => {
        if (editData?.city && subRegionOptions.length > 0 && selectedRegion === editData.city.regionId && !selectedSubRegion) {
            setValue('subregion', editData.city.subregionId);
            setSelectedSubRegion(editData.city.subregionId);
        }
    }, [subRegionOptions.length, editData?.city, selectedRegion, selectedSubRegion]);

    useEffect(() => {
        if (editData?.city && cityOptions.length > 0 && selectedSubRegion === editData.city.subregionId && !selectedCity) {
            setValue('cityId', editData.city.id);
            setSelectedCity(editData.city.id);
        }
    }, [cityOptions.length, editData?.city, selectedSubRegion, selectedCity]);

    const submitForm = async (data) => {
        try {
            const formData = {
                costCenterCode: data.costCenterCode,
                name: data.name,
                address: data.address,
                phone: data.phone,
                region: data.region,
                subregion: data.subregion,
                cityId: data.cityId,
                postalCode: data.postalCode,
                activity: data.activity,
                ...(isEdit && {
                    dateClosing: data.dateClosing,
                    reasonClosing: data.reasonClosing,
                }),
            };
            if (onConfirm) {
                await onConfirm(formData);
            }
        } catch (error) {
            console.error('Error saving company center:', error);
        }
    };

    const title = isEdit ? t('common.edit') : t('common.add');

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">{title} {t('organizationalStructure.companyBranch.companyCenters.title')}</h4>
                        <form id="companyCentersForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="form-sections">
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.companyBranch.companyCenters.costCenterCode')} <span className="required">*</span></span>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.costCenterCode ? 'is-invalid' : ''}`}
                                            maxLength={5}
                                            {...register('costCenterCode')}
                                        />
                                        {errors.costCenterCode && <p className="text-danger">{errors.costCenterCode.message}</p>}
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.companyBranch.companyCenters.name')} <span className="required">*</span></span>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            {...register('name')}
                                        />
                                        {errors.name && <p className="text-danger">{errors.name.message}</p>}
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.companyBranch.companyCenters.address')} </span>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                                            {...register('address')}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.companyBranch.companyCenters.phone')} </span>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                            {...register('phone')}
                                        />
                                    </div>
                                    {isEdit && (
                                        <div className="form-group">
                                            <span className="span-bold">{t('organizationalStructure.companyBranch.companyCenters.closingDate')}</span>
                                            <CustomDatePicker
                                                type="date"
                                                name="dateClosing"
                                                value={watch('dateClosing')}
                                                onChange={(date) => setValue('dateClosing', date, { shouldValidate: true })}
                                                errors={errors}
                                            />
                                        </div>
                                    )}
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.companyBranch.companyCenters.status')}</span>
                                        <select
                                            className={`form-control ${errors.activity ? 'is-invalid' : ''}`}
                                            {...register('activity', { valueAsNumber: true })}
                                        >
                                            <option value={0}>{t('organizationalStructure.companyBranch.companyCenters.active')}</option>
                                            <option value={1}>{t('organizationalStructure.companyBranch.companyCenters.closed')}</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('persons.form.region')}</span>
                                        <select
                                            className={`form-control ${errors.region ? 'is-invalid' : ''}`}
                                            {...register('region')}
                                        >
                                            <option value="">{t('common.select')}</option>
                                            {regionOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('persons.form.municipality')}</span>
                                        <select
                                            className={`form-control ${errors.subregion ? 'is-invalid' : ''}`}
                                            {...register('subregion')}
                                            disabled={!region || regionOptions.length === 0}
                                        >
                                            <option value="">{t('common.select')}</option>
                                            {subRegionOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('persons.form.city')}</span>
                                        <select
                                            className={`form-control ${errors.cityId ? 'is-invalid' : ''}`}
                                            {...register('cityId')}
                                            disabled={!subregion || subRegionOptions.length === 0}
                                        >
                                            <option value="">{t('common.select')}</option>
                                            {cityOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.companyBranch.companyCenters.postalCode')}</span>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.postalCode ? 'is-invalid' : ''}`}
                                            {...register('postalCode')}
                                            disabled={true}
                                        />
                                    </div>
                              
                                    {isEdit && (
                                        <div className="form-group">
                                            <span className="span-bold">{t('organizationalStructure.companyBranch.companyCenters.closingReason')}</span>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.reasonClosing ? 'is-invalid' : ''}`}
                                                {...register('reasonClosing')}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="submit" form="companyCentersForm" className="btn btn-primary">
                            {t('common.save')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}